import React, { useState, useEffect } from 'react';
import ApiClient from '../../methods/api/apiClient';
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import loader from '../../methods/loader';
import { decryptId } from '../../components/common/Encryption/encryption';
import RegistrationCard from '../RegistrationList/RegistrationCard';

const RequestGroup = ({ eventDetail = '', data = '', setRequestGroup = '', connectMeetLabel = 'ConnectMeet' }: any) => {
  const { eventid } = useParams();
  const user = useSelector((state: any) => state.user);
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [selectedCheckbox, setSelectedCheckbox] = useState("");
  const [selectRequiredError, setSelectRequiredError] = useState(false);
  const [acList, setAcList] = useState<any[]>([]);
  const [id, setId] = useState<string | null>(null);
  useEffect(() => {
      if (eventid) {
          decryptId(eventid).then(decrypted => setId(decrypted));
      }
  }, [eventid]);
  const groupId = data?.groupId?._id;

  // cw: Fetch user's Already Connected list
  useEffect(() => {
    if (user?._id && groupId) {
      ApiClient.get(`api/exclusions/list?groupId=${groupId}&userId=${user._id}`).then((res: any) => {
        if (res?.success && res?.data) {
          setAcList(res.data);
        }
      }).catch(() => {});
    }
  }, [user?._id, groupId]);

  // cw: Load existing request so user sees their previous selections
  useEffect(() => {
    if (user?.email && id && groupId) {
      ApiClient.get(`api/group-request/list?eventId=${id}&requestedBy=${user.email}&groupId=${groupId}`).then((res: any) => {
        if (res?.success && res?.data?.length > 0) {
          const existing = res.data[0];
          if (existing.requestType) {
            setSelectedCheckbox(existing.requestType);
          }
          if (existing.attendeesDetails?.length > 0) {
            const ids = existing.attendeesDetails
              .filter((a: any) => a._id)
              .map((a: any) => a._id.toString());
            if (ids.length > 0) setSelectedIds(ids);
          }
        }
      }).catch(() => {});
    }
  }, [user?.email, id, groupId]);

  // Show all attendees, not just those who RSVP'd Yes
  const allAttendees = [...(eventDetail('Yes') || []), ...(eventDetail('Pending') || []), ...(eventDetail('No') || [])];
  
  // Remove duplicates by id
  const uniqueAttendees = allAttendees.filter((itm: any, index: number, self: any[]) => 
    index === self.findIndex((t: any) => (t.id || t._id) === (itm.id || itm._id))
  );
  
  // Full attendee rows (with memberDetails) for the photolist picker, minus the current user.
  const selectableAttendees = uniqueAttendees
    .filter((itm: any) => ((itm.memberDetails?.email || itm?.email) !== user?.email))
    .sort((a: any, b: any) => {
      const an = (a.memberDetails?.fullName || a?.fullName || '').toLowerCase();
      const bn = (b.memberDetails?.fullName || b?.fullName || '').toLowerCase();
      return an.localeCompare(bn);
    });

  // cw: Filter the photolist by the search box (name AND their info). Case-insensitive
  // substring match; selection state is unaffected (hidden people stay selected).
  const query = search.trim().toLowerCase();
  const matchesSearch = (itm: any) => {
    if (!query) return true;
    const md = itm?.memberDetails || {};
    const pd = md?.profileData || {};
    const fields = [
      itm?.fullName,
      md?.fullName,
      md?.company,
      itm?.companyName,
      md?.companyName,
      md?.profession,
      pd?.headline,
      pd?.industry,
      md?.email,
      itm?.email,
    ];
    return fields.some((f: any) => (f ? String(f).toLowerCase().indexOf(query) !== -1 : false));
  };
  const filteredAttendees = selectableAttendees.filter(matchesSearch);

  // cw: Check if a member is on the user's AC list (by userId)
  const getAcExclusion = (userId: string) => {
    if (!userId) return null;
    return acList.find((excl: any) =>
      excl.excludedUserId?._id?.toString() === userId?.toString() ||
      excl.excludedUserId?.toString() === userId?.toString()
    );
  };

  const isSelected = (itm: any) => {
    const val = itm.id || itm._id;
    return selectedIds.map((x: any) => String(x)).includes(String(val));
  };

  // Toggle one person in/out of the selection (photolist card click). Runs the same
  // Already-Connected check as before when adding someone.
  const toggleMember = (itm: any) => {
    const val = itm.id || itm._id;
    const key = String(val);
    if (selectedIds.map((x: any) => String(x)).includes(key)) {
      setSelectedIds((prev: any[]) => prev.filter((x: any) => String(x) !== key));
      return;
    }
    const memberUserId = itm.memberDetails?._id || itm.userId;
    const excl = memberUserId ? getAcExclusion(memberUserId) : null;
    if (excl) {
      const memberName = itm.memberDetails?.fullName || itm?.fullName || 'This member';
      Swal.fire({
        title: 'Already Connected',
        text: `${memberName} is on your Already Connected list.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove From AC',
        cancelButtonText: 'Cancel Request',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
      }).then((result) => {
        if (result.isConfirmed) {
          ApiClient.put('api/exclusions/remove', { id: excl._id, userId: user._id }).then((res: any) => {
            if (res?.success) {
              setAcList(acList.filter((e: any) => e._id !== excl._id));
              setSelectedIds((prev: any[]) => [...prev, val]);
              toast.success('Removed from Already Connected list');
            } else {
              toast.error(res?.message || 'Failed to remove from AC list');
            }
          }).catch(() => toast.error('Failed to remove from AC list'));
        }
      });
      return;
    }
    setSelectedIds((prev: any[]) => [...prev, val]);
    setSelectRequiredError(false);
  };

  const handleSubmit = () => {
    if (selectedCheckbox === "connect_with_members" && selectedIds.length === 0) {
      setSelectRequiredError(true);
      return;
    }

    loader(true);
    let payload = {
      eventId: id,
      groupId: data?.groupId?._id,
      requestedBy: user?.email,
      attendeeIds: selectedIds,
      userId: user?._id,
      requestType: selectedCheckbox
    };

    ApiClient.post("api/group/request", payload).then(res => {
      if (res) {
        toast(res.message);
        loader(false);
        setRequestGroup(false);
      }
    });
  };

  const handleCheckboxChange = (checkboxName: string) => {
    setSelectedCheckbox(checkboxName);
  };

  const isSubmitDisabled = !selectedCheckbox; // Disable if no checkbox is selected

  return (
    <div className='flex flex-col gap-4 mt-4'>
      <div>
        <div className='flex gap-2 items-center'>
          <input
            className='h-4 w-4 cursor-pointer'
            type="radio"
            checked={selectedCheckbox === "connect_with_members"}
            onChange={() => handleCheckboxChange("connect_with_members")}
          />
          <span>Request specific people for my {connectMeetLabel} for this event</span>
        </div>
        {selectedCheckbox === "connect_with_members" && (
          <div className='mt-2'>
            <p className="text-sm mb-2 font-medium">Select the people to request (scroll to view everyone):</p>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="I'm looking for… (name)"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-1 focus:ring-[#EF7A2B]"
            />
            {selectableAttendees.length > 0 && (
              <p className="text-xs text-gray-500 mb-2">{filteredAttendees.length} of {selectableAttendees.length}</p>
            )}
            <div className="max-h-96 overflow-y-auto flex flex-col gap-2 pr-1">
              {selectableAttendees.length === 0 && (
                <p className="text-sm text-gray-500">No other attendees available</p>
              )}
              {selectableAttendees.length > 0 && filteredAttendees.length === 0 && (
                <p className="text-sm text-gray-500">No matches</p>
              )}
              {filteredAttendees.map((itm: any) => (
                <RegistrationCard
                  key={itm.id || itm._id}
                  member={itm}
                  connectMeetLabel={connectMeetLabel}
                  testimonialLabel={''}
                  canAct={false}
                  selectable
                  selected={isSelected(itm)}
                  onToggleSelect={() => toggleMember(itm)}
                  onRequestCm={() => {}}
                  onRequestTy={() => {}}
                />
              ))}
            </div>
            {selectRequiredError && <p className="text-red-500 mt-1">Please select at least one member.</p>}
          </div>
        )}
      </div>

      <div>
        <div className='flex gap-2 items-center'>
          <input
            className='h-4 w-4 cursor-pointer'
            type="radio"
            checked={selectedCheckbox === "not_interested"}
            onChange={() => handleCheckboxChange("not_interested")}
          />
          <span>I'm NOT available for {'aeiouAEIOU'.includes(connectMeetLabel?.[0]) ? 'an' : 'a'} {connectMeetLabel} for this event</span>
        </div>
      </div>

      <div>
        <div className='flex gap-2 items-center'>
          <input
            className='h-4 w-4 cursor-pointer'
            type="radio"
            checked={selectedCheckbox === "system_generate"}
            onChange={() => handleCheckboxChange("system_generate")}
          />
          <span>I'm available for {'aeiouAEIOU'.includes(connectMeetLabel?.[0]) ? 'an' : 'a'} {connectMeetLabel} for this event</span>
        </div>
      </div>
    
      <div className='mt-4 flex items-center justify-end'>
        <button
          className='bg-[#EF7A2B] inline-flex py-2 px-6 flex items-center justify-center gap-x-2 text-white shadow hover:shadow-lg rounded-xl'
          onClick={handleSubmit}
          disabled={isSubmitDisabled} // Disable if no checkbox is selected
        >
          Submit My Request
        </button>
      </div>
    </div>
  );
};

export default RequestGroup;
